/** Stable public identities shared by API, local UI and remote-house UI.
 * Existing slots never depend on registry ordering. New accounts can declare
 * access_number/house in the registry; unknown accounts keep an explicit ID. */
export type AccountHouse = 'dev' | 'partner';
export const ACCOUNT_IDENTITIES: Record<string, { house: AccountHouse; number: number }> = {
  engelmann: { house: 'dev', number: 1 }, office: { house: 'dev', number: 2 },
  gmail: { house: 'dev', number: 3 }, werking: { house: 'dev', number: 4 },
  sahori: { house: 'partner', number: 1 }, kurt: { house: 'partner', number: 2 },
  coach: { house: 'partner', number: 3 }, erk: { house: 'partner', number: 4 },
  'codex-gmail': { house: 'dev', number: 1 },
};
/** Neutraler Zugangsschluessel (`ki-2`, `partner-ki-1`, `cx-1`, `gm-1`): ersetzt die interne
 * Kennung dort, wo ein Nicht-Admin sie nicht sehen soll (server/lib/verbrauch-sicht.ts).
 * `accountIdentity()` loest ihn zu demselben Etikett auf wie die echte Kennung. */
const NEUTRAL = /^(?:(dev|partner)-)?(ki|cx|gm)-([1-9][0-9]*)$/;
const NEUTRAL_ART = { ki: 'claude', cx: 'codex', gm: 'gemini' } as const;
export function neutralerZugangsSchluessel(id: string, provider = 'claude', configured?: { house?: AccountHouse; accessNumber?: number }, lokal = true): string | null {
  const identity = accountIdentity(id, provider, configured);
  if (!identity.accessNumber) return null;
  const art = provider === 'codex' || id.startsWith('codex-') ? 'cx' : provider === 'gemini' ? 'gm' : 'ki';
  return `${!lokal && identity.house ? `${identity.house}-` : ''}${art}-${identity.accessNumber}`;
}

export function accountIdentity(id: string, provider = 'claude', configured?: { house?: AccountHouse; accessNumber?: number }) {
  const neutral = ACCOUNT_IDENTITIES[id] || configured ? null : NEUTRAL.exec(id);
  if (neutral) provider = NEUTRAL_ART[neutral[2] as keyof typeof NEUTRAL_ART];
  const fixed = ACCOUNT_IDENTITIES[id] ?? (neutral ? { house: neutral[1] as AccountHouse | undefined, number: Number(neutral[3]) } : undefined);
  const house = configured?.house ?? fixed?.house;
  const number = configured?.accessNumber ?? fixed?.number;
  const name = provider === 'codex' || id.startsWith('codex-') ? 'Codex-Zugang' : provider === 'gemini' ? 'Gemini-Zugang' : 'KI-Zugang';
  const label = id === 'auto' ? 'Automatisch' : id === 'local' ? 'Lokal' : `${name} ${number ?? `(${id})`}`;
  return { id, house, accessNumber: number, label, qualifiedLabel: house ? `${house === 'partner' ? 'Partner' : 'Dev'} · ${label}` : label };
}

/** Resolve human labels only within this house; technical IDs remain valid. */
export function resolveAccountReference(reference: string, accounts: { id: string; provider?: string; house?: AccountHouse; accessNumber?: number }[], house: AccountHouse): string | null {
  if (reference === 'auto') return 'auto';
  if (accounts.some(a => a.id === reference)) return reference;
  const normalized = reference.toLowerCase().replace(/[\s·-]/g, '');
  const match = /^(p|partner|d|dev)?(ki|codex|gemini)(?:zugang)?([1-9][0-9]*)$/.exec(normalized);
  if (!match) return null;
  if (match[1] && (['p', 'partner'].includes(match[1]) ? 'partner' : 'dev') !== house) return null;
  const candidates = accounts.filter(a => (a.provider || 'claude') === ({ ki: 'claude', codex: 'codex', gemini: 'gemini' }[match[2]]) && accountIdentity(a.id, a.provider, a).accessNumber === Number(match[3]));
  return candidates.length === 1 ? candidates[0].id : null;
}
